import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageSEO from '@/components/feature/PageSEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, articleCategories } from '@/mocks/articles';

export default function ArticleListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') || 'all';
  const [animateKey, setAnimateKey] = useState(0);

  const filteredArticles = activeCategory === 'all'
    ? articles
    : articles.filter((a) => {
        const cat = articleCategories.find((c) => c.slug === activeCategory);
        return cat ? a.category === cat.name : true;
      });

  const handleCategoryChange = (slug: string) => {
    setSearchParams({ category: slug });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setAnimateKey((prev) => prev + 1);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      <PageSEO
        title="游戏资讯 - galagame | 最新游戏动态、活动与攻略"
        description="galagame 游戏资讯中心，提供最新的游戏动态、平台活动、电竞资讯、游戏攻略等内容。关注 galagame 资讯，不错过任何精彩内容。"
        keywords="galagame资讯,galagame活动,galagame攻略,galagame电竞,galagame最新消息"
        canonicalUrl="https://www.galagame.com/articles"
      />
      <ScrollToTop />
      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { name: '首页', path: '/' },
            { name: '游戏资讯' },
          ]}
        />

        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-100">
                游戏资讯
              </h1>
              <p className="mt-2 text-sm text-foreground-500">
                了解 galagame 平台最新动态与游戏资讯
              </p>
            </div>

            <div className="flex items-center justify-center gap-1.5 mb-10 flex-wrap">
              {articleCategories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-400 hover:text-foreground-100 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" key={animateKey}>
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/50 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-0.5 text-[10px] font-medium rounded-full bg-accent-500/80 text-background-50">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-foreground-500 mb-2">
                      <span>{article.date}</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-600"></span>
                      <span>{article.readTime}阅读</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-600"></span>
                      <span>{article.author}</span>
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-100 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-article-line text-2xl text-foreground-500"></i>
                </div>
                <p className="text-foreground-500">该分类暂未发布文章，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}